import { FormControl, MenuItem, Select, Switch, TextField, SelectChangeEvent, styled } from '@mui/material';
import { useState, useEffect } from 'react';
import { RouteDto } from 'src/generated/services/TFinancialApi';

interface TradeLaneZoneSelectorProps {
  routes: RouteDto[];
  value?: string;
  onChange?: (value: string) => void;
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  minWidth: '70px',
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiInputBase-input': {
    paddingTop: '4px!important',
    paddingBottom: '4px!important',
    paddingLeft: '0px!important',
    paddingRight: '0px!important',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0',
    borderBottom: '1px solid #999999',
    borderRadius: '0',
  },
}));

const StyledSelect = styled(Select<unknown>)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiInputBase-input': {
    paddingTop: '4px!important',
    paddingBottom: '4px!important',
    paddingLeft: '0px!important',
    paddingRight: '0px!important',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0',
    borderBottom: '1px solid #999999',
    borderRadius: '0',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
}));

/**
 * TradeLaneZoneSelector Component
 *
 * This component provides two modes for selecting a trade lane zone:
 * 1. **Zone Mode**: Allows the user to select a trade lane zone from a list of routes.
 * 2. **Trade Lane Mode**: Allows the user to manually input custom trade lane values via two text fields.
 *
 * The mode is determined by a toggle switch. If the current value contains a colon (':'),
 * it indicates that the component is in Trade Lane mode, where the value format is "sgsin:usffo".
 *
 * Props:
 * - routes: An array of RouteDto objects representing available trade lane zones.
 * - value (optional): The current value. In Trade Lane mode, this should be in the format "sgsin:usffo".
 * - onChange (optional): Callback function that is triggered when the selected value changes.
 *
 * Usage:
 * The component will render text fields for manual input in Trade Lane mode, or a select dropdown
 * populated with the provided routes in Zone mode. Switching between modes resets the corresponding fields.
 *
 * @param {TradeLaneZoneSelectorProps} props - Component properties.
 * @returns {JSX.Element} The rendered TradeLaneZoneSelector component.
 */

export const TradeLaneZoneSelector = ({ routes, value, onChange }: TradeLaneZoneSelectorProps) => {
  // Parse initial value if it contains underscore
  const [isTradeLane, setIsTradeLane] = useState(value?.includes(':') ?? false);
  const [sgsin, setSgsin] = useState(value?.split(':')[0] || '');
  const [usffo, setUsffo] = useState(value?.split(':')[1] || '');
  const [selectedZone, setSelectedZone] = useState(value || '');

  /**
   * handleZoneChange
   *
   * Handles changes in the dropdown select for Zone mode.
   * Updates the selected zone state and notifies the parent component via onChange.
   *
   * @param {SelectChangeEvent<unknown>} event - The change event from the select.
   * @param {React.ReactNode} child - The child node (not used in this implementation).
   */

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleZoneChange = (event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
    const newValue = event.target.value as string;
    setSelectedZone(newValue);
    onChange?.(newValue);
  };

  /**
   * handleTradeLaneChange
   *
   * Handles updates to either of the trade lane input fields.
   * Updates the sgsin and usffo states and concatenates them into the final value,
   * then calls the onChange callback with the updated "sgsin:usffo" string.
   *
   * @param {string} newSgsin - The new value for sgsin.
   * @param {string} newUsffo - The new value for usffo.
   */

  const handleTradeLaneChange = (newSgsin: string, newUsffo: string) => {
    setSgsin(newSgsin);
    setUsffo(newUsffo);
    onChange?.(`${newSgsin}:${newUsffo}`);
  };

  /**
   * Effect to synchronize state when switching between Trade Lane and Zone modes.
   *
   * When switching to Trade Lane mode:
   * - If the current value does not include a colon, clear the sgsin and usffo fields.
   *
   * When switching to Zone mode:
   * - If the current value includes a colon, clear the selectedZone.
   */
  useEffect(() => {
    if (isTradeLane && value && !value.includes(':')) {
      // When switching to trade lane mode, clear the fields if value doesn't contain underscore
      setSgsin('');
      setUsffo('');
      onChange?.('');
    } else if (!isTradeLane && value?.includes(':')) {
      // When switching to zone mode, clear the selected value if it contains underscore
      setSelectedZone('');
      onChange?.('');
    }
  }, [isTradeLane, value, onChange]);

  const renderInputField = () => {
    if (isTradeLane) {
      return (
        <div className='flex items-center gap-4'>
          <StyledTextField variant='outlined' size='small' value={sgsin} onChange={(e) => handleTradeLaneChange(e.target.value, usffo)} />
          <span className='text-16 xl:text-20 mx-8 xl:mx-10'>/</span>
          <StyledTextField variant='outlined' size='small' value={usffo} onChange={(e) => handleTradeLaneChange(sgsin, e.target.value)} />
        </div>
      );
    }

    if (routes.length > 0) {
      return (
        <FormControl variant='outlined' size='small' sx={{ minWidth: 160 }}>
          <StyledSelect value={selectedZone} onChange={handleZoneChange}>
            {routes.map((route) => (
              <MenuItem key={route.route} value={route.route}>
                {route.route}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      );
    }

    return (
      <StyledTextField
        variant='outlined'
        size='small'
        value={selectedZone}
        onChange={(e) => {
          setSelectedZone(e.target.value);
          onChange?.(e.target.value);
        }}
        placeholder='Enter zone'
        sx={{ minWidth: 160 }}
      />
    );
  };

  return (
    <div className='flex items-center gap-4'>
      {renderInputField()}
      <Switch checked={isTradeLane} onChange={(e) => setIsTradeLane(e.target.checked)} color='primary' />
    </div>
  );
};
